import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/lib/context/auth-context";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Course, StateRequirements } from "@/pages/admin/types";
import {
  Calendar, Users, GraduationCap, Award,
  CheckCircle, TrendingUp, Clock, BookOpen, Compass, ExternalLink, BarChart3
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  PieChart, Pie, BarChart, Bar, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  Cell, Area, AreaChart
} from "recharts";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";

const Analytics = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<"week" | "month" | "year" | "all">("month");
  const [activeIndex, setActiveIndex] = useState(0);
  
  const isStaff = profile?.role === 'staff' || profile?.role === 'admin';

  // Function to safely parse state requirements
  const safeParseStateRequirements = (stateReqJson: any): StateRequirements => {
    try {
      if (!stateReqJson) {
        // Return default structure if null
        return {
          behavioralIntervention: false,
          readingPreparation: false,
          keyWarningSigns: false, 
          suicidePrevention: false,
          englishLearners: false,
          americanIndianHistory: false,
          culturalCompetency: {
            enabled: false,
            racial: false,
            religion: false,
            systemicRacism: false,
            genderIdentity: false,
            sexualOrientation: false,
            languageDiversity: false,
            disabilities: false
          }
        };
      }
      
      // Handle case when stateReqJson is already parsed
      if (typeof stateReqJson === 'object') {
        return stateReqJson as StateRequirements;
      }
      
      // Try to parse JSON string
      const parsed = typeof stateReqJson === 'string' ? JSON.parse(stateReqJson) : stateReqJson;
      
      // Ensure the object has the expected structure
      return {
        behavioralIntervention: Boolean(parsed.behavioralIntervention),
        behavioralInterventionHours: parsed.behavioralInterventionHours,
        readingPreparation: Boolean(parsed.readingPreparation),
        readingPreparationHours: parsed.readingPreparationHours,
        keyWarningSigns: Boolean(parsed.keyWarningSigns),
        keyWarningSignsHours: parsed.keyWarningSignsHours,
        suicidePrevention: Boolean(parsed.suicidePrevention),
        suicidePreventionHours: parsed.suicidePreventionHours,
        englishLearners: Boolean(parsed.englishLearners),
        englishLearnersHours: parsed.englishLearnersHours,
        americanIndianHistory: Boolean(parsed.americanIndianHistory),
        americanIndianHistoryHours: parsed.americanIndianHistoryHours,
        culturalCompetency: {
          enabled: Boolean(parsed.culturalCompetency?.enabled),
          enabledHours: parsed.culturalCompetency?.enabledHours,
          racial: Boolean(parsed.culturalCompetency?.racial),
          religion: Boolean(parsed.culturalCompetency?.religion),
          systemicRacism: Boolean(parsed.culturalCompetency?.systemicRacism),
          genderIdentity: Boolean(parsed.culturalCompetency?.genderIdentity),
          sexualOrientation: Boolean(parsed.culturalCompetency?.sexualOrientation),
          languageDiversity: Boolean(parsed.culturalCompetency?.languageDiversity),
          disabilities: Boolean(parsed.culturalCompetency?.disabilities)
        }
      };
    } catch (error) {
      console.error("Error parsing state requirements:", error, stateReqJson);
      // Return default structure on error
      return {
        behavioralIntervention: false,
        readingPreparation: false,
        keyWarningSigns: false,
        suicidePrevention: false,
        englishLearners: false,
        americanIndianHistory: false,
        culturalCompetency: {
          enabled: false,
          racial: false,
          religion: false,
          systemicRacism: false,
          genderIdentity: false,
          sexualOrientation: false,
          languageDiversity: false,
          disabilities: false
        }
      };
    }
  };

  // Format date range for query based on timeRange
  const getDateRangeFilter = () => {
    const now = new Date();
    let startDate = new Date();
    
    switch(timeRange) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'all':
      default:
        startDate = new Date(2000, 0, 1); // Far in the past to get all data
    }
    
    return {
      startDateISO: startDate.toISOString(),
      endDateISO: now.toISOString()
    };
  };

  const { data: staffAnalytics = {
    coursesByCategory: [],
    registrationsTrend: [],
    attendanceTrend: [],
    courseTypeDistribution: []
  }, isLoading: isLoadingStaffData } = useQuery({
    queryKey: ['staffAnalytics', timeRange],
    queryFn: async () => {
      if (!isStaff) return {
        coursesByCategory: [],
        registrationsTrend: [],
        attendanceTrend: [],
        courseTypeDistribution: []
      };

      try {
        console.log("Fetching staff analytics data for time range:", timeRange);
        const dateRange = getDateRangeFilter();
        
        // Fetch courses data
        const { data: courses, error: coursesError } = await supabase
          .from('courses')
          .select('id, title, state_requirements, event_type, created_at, status, hours')
          .gte('created_at', dateRange.startDateISO)
          .lte('created_at', dateRange.endDateISO)
          .eq('status', 'published');

        if (coursesError) {
          console.error("Error fetching courses:", coursesError);
          throw new Error(coursesError.message);
        }

        console.log(`Fetched ${courses?.length || 0} courses`);

        // Process category counts based on state requirements
        const categoryCounts: { [key: string]: number } = {
          'Positive Behavioral Intervention': 0,
          'Reading Preparation': 0,
          'Key Warning Signs': 0,
          'Suicide Prevention': 0,
          'English Learners': 0,
          'American Indian History': 0,
          'Cultural Competency': 0,
          'Other': 0
        };

        if (courses) {
          courses.forEach(course => {
            let categoryFound = false;
            try {
              const requirements = safeParseStateRequirements(course.state_requirements);

              if (requirements.behavioralIntervention) {
                categoryCounts['Positive Behavioral Intervention']++;
                categoryFound = true;
              }
              if (requirements.readingPreparation) {
                categoryCounts['Reading Preparation']++;
                categoryFound = true;
              }
              if (requirements.keyWarningSigns) {
                categoryCounts['Key Warning Signs']++;
                categoryFound = true;
              }
              if (requirements.suicidePrevention) {
                categoryCounts['Suicide Prevention']++;
                categoryFound = true;
              }
              if (requirements.englishLearners) {
                categoryCounts['English Learners']++;
                categoryFound = true;
              }
              if (requirements.americanIndianHistory) {
                categoryCounts['American Indian History']++;
                categoryFound = true;
              }
              if (requirements.culturalCompetency?.enabled) {
                categoryCounts['Cultural Competency']++;
                categoryFound = true;
              }
            } catch (e) {
              console.error("Error processing state requirements for course:", course.id, e);
            }

            if (!categoryFound) {
              categoryCounts['Other']++;
            }
          });
        }

        // Convert category counts to array format for charts
        const coursesByCategory = Object.entries(categoryCounts)
          .filter(([_, value]) => value > 0)
          .map(([name, value]) => ({
            name,
            value,
            percent: 0, // Will calculate below
          }));
        
        // Calculate percentages for pie chart
        const totalCourses = coursesByCategory.reduce((sum, item) => sum + item.value, 0);
        coursesByCategory.forEach(item => {
          item.percent = totalCourses > 0 ? item.value / totalCourses : 0;
        });

        // Fetch registrations data
        const { data: registrations, error: regError } = await supabase
          .from('course_registrations')
          .select('id, status, registration_date, attendance_date, course_id, verified_at')
          .gte('registration_date', dateRange.startDateISO)
          .lte('registration_date', dateRange.endDateISO);

        if (regError) {
          console.error("Error fetching registrations:", regError);
          throw new Error(regError.message);
        }

        console.log(`Fetched ${registrations?.length || 0} registrations`);

        // Calculate course type distribution
        const typeDistribution = courses ? courses.reduce((acc, course) => {
          const type = course.event_type || 'other';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {} as Record<string, number>) : {};

        const courseTypeDistribution = Object.entries(typeDistribution).map(([name, value]) => ({
          name: name === 'live' ? 'Live Events' : 'Self-Paced',
          value,
        }));

        // Generate month labels for trends
        const now = new Date();
        const months = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push(d.toLocaleString('default', { month: 'short' }));
        }

        // Calculate registration trends by month
        const registrationsTrend = months.map(month => {
          const registrationsCount = registrations ? registrations.filter(reg => {
            const date = new Date(reg.registration_date);
            return date.toLocaleString('default', { month: 'short' }) === month;
          }).length : 0;

          return {
            name: month,
            registrations: registrationsCount,
          };
        });

        // Calculate attendance vs. registration trends
        const attendanceTrend = months.map(month => {
          const totalRegistrations = registrations ? registrations.filter(reg => {
            const date = new Date(reg.registration_date);
            return date.toLocaleString('default', { month: 'short' }) === month;
          }).length : 0;

          const attendedRegistrations = registrations ? registrations.filter(reg => {
            const date = new Date(reg.registration_date);
            return date.toLocaleString('default', { month: 'short' }) === month && 
                  (reg.verified_at !== null || reg.status === 'completed');
          }).length : 0;

          return {
            name: month,
            total: totalRegistrations,
            attended: attendedRegistrations,
          };
        });

        return {
          coursesByCategory,
          registrationsTrend,
          attendanceTrend,
          courseTypeDistribution
        };
      } catch (error) {
        console.error('Error fetching staff analytics:', error);
        toast({
          title: "Error loading analytics",
          description: "Unable to load analytics data. Please try again later.",
          variant: "destructive",
        });
        return {
          coursesByCategory: [],
          registrationsTrend: [],
          attendanceTrend: [],
          courseTypeDistribution: []
        };
      }
    },
    enabled: isStaff,
  });

  // User analytics query
  const { data: userAnalytics = {
    completedCoursesTrend: [],
    coursesCategoryDistribution: [],
    hoursCompletedTrend: [],
    progressStats: { completed: 0, inProgress: 0, registered: 0, total: 0 }
  }, isLoading: isLoadingUserData } = useQuery({
    queryKey: ['userAnalytics', user?.id, timeRange],
    queryFn: async () => {
      if (!user?.id) return {
        completedCoursesTrend: [],
        coursesCategoryDistribution: [],
        hoursCompletedTrend: [],
        progressStats: { completed: 0, inProgress: 0, registered: 0, total: 0 }
      };

      try {
        console.log("Fetching user analytics data for user:", user.id);
        const dateRange = getDateRangeFilter();
        
        // Fetch user registrations
        const { data: userRegistrations, error: regError } = await supabase
          .from('course_registrations')
          .select(`
            id, 
            status, 
            registration_date, 
            course_id,
            verified_at,
            courses(id, title, category, hours, state_requirements)
          `)
          .eq('user_id', user.id)
          .gte('registration_date', dateRange.startDateISO)
          .lte('registration_date', dateRange.endDateISO);

        if (regError) {
          console.error("Error fetching user registrations:", regError);
          throw new Error(regError.message);
        }

        console.log(`Fetched ${userRegistrations?.length || 0} user registrations`);

        // Fetch user certificates
        const { data: certificates, error: certError } = await supabase
          .from('certificates')
          .select(`
            id, 
            issue_date,
            course_id,
            courses(id, title, category, hours, state_requirements)
          `)
          .eq('user_id', user.id)
          .gte('issue_date', dateRange.startDateISO)
          .lte('issue_date', dateRange.endDateISO);

        if (certError) {
          console.error("Error fetching user certificates:", certError);
          throw new Error(certError.message);
        }

        console.log(`Fetched ${certificates?.length || 0} user certificates`);

        // Process category distribution
        const categoriesMap = userRegistrations ? userRegistrations.reduce((acc, reg) => {
          const category = reg.courses?.category || 'Uncategorized';
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {} as Record<string, number>) : {};

        const coursesCategoryDistribution = Object.entries(categoriesMap).map(([name, value]) => ({
          name,
          value,
        }));

        // Calculate progress statistics
        const progressStats = {
          completed: certificates?.length || 0,
          inProgress: userRegistrations ? userRegistrations.filter(r => r.status === 'in_progress').length : 0,
          registered: userRegistrations ? userRegistrations.filter(r => r.status === 'registered').length : 0,
          total: userRegistrations?.length || 0
        };

        // Generate month labels for trends
        const now = new Date();
        const months = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push(d.toLocaleString('default', { month: 'short' }));
        }

        // Calculate completed courses trend
        const completedCoursesTrend = months.map(month => {
          const completedCount = certificates ? certificates.filter(cert => {
            const date = new Date(cert.issue_date);
            return date.toLocaleString('default', { month: 'short' }) === month;
          }).length : 0;

          return {
            name: month,
            completed: completedCount,
          };
        });

        // Calculate hours completed trend
        const hoursCompletedTrend = months.map(month => {
          const hoursCompleted = certificates ? certificates
            .filter(cert => {
              const date = new Date(cert.issue_date);
              return date.toLocaleString('default', { month: 'short' }) === month;
            })
            .reduce((sum, cert) => sum + (cert.courses?.hours || 0), 0) : 0;

          return {
            name: month,
            hours: hoursCompleted,
          };
        });

        return {
          completedCoursesTrend,
          coursesCategoryDistribution,
          hoursCompletedTrend,
          progressStats
        };
      } catch (error) {
        console.error('Error fetching user analytics:', error);
        toast({
          title: "Error loading analytics",
          description: "Unable to load analytics data. Please try again later.",
          variant: "destructive",
        });
        return {
          completedCoursesTrend: [],
          coursesCategoryDistribution: [],
          hoursCompletedTrend: [],
          progressStats: { completed: 0, inProgress: 0, registered: 0, total: 0 }
        };
      }
    },
    enabled: !isStaff && !!user?.id,
  });

  // Brand colors for charts
  const BRAND_COLORS = [
    "#004161",
    "#cfb9a2",
    "#0f8f8c",
    "#b1c3cf",
    "#f1eceb",
    "#005b8c",
    "#0a7d7a",
    "#7e939e",
    "#d8a583",
  ];

  // Category-specific colors
  const CATEGORY_COLORS = {
    'Positive Behavioral Intervention': '#0f8f8c',
    'Reading Preparation': '#004161',
    'Key Warning Signs': '#cfb9a2',
    'Suicide Prevention': '#b1c3cf',
    'English Learners': '#005b8c',
    'American Indian History': '#0a7d7a',
    'Cultural Competency': '#7e939e',
    'Other': '#d8a583',
  };

  const getCategoryColor = (name: string) => {
    return CATEGORY_COLORS[name as keyof typeof CATEGORY_COLORS] || '#004161';
  };

  // Custom tooltip component for pie charts
  const CustomPieTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded shadow-lg border border-gray-200">
          <p className="font-title font-bold text-primary">{`${payload[0].name}`}</p>
          <p className="font-subtitle text-muted-foreground">{`Courses: ${payload[0].value}`}</p>
          <p className="font-subtitle text-muted-foreground">{`Percentage: ${(payload[0].payload.percent * 100).toFixed(1)}%`}</p>
        </div>
      );
    }
    return null;
  };

  // Handle pie chart interactions
  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  // Render a simple pie chart component
  const renderSimplePieChart = (data: any[]) => (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          outerRadius={80}
          fill="#004161"
          dataKey="value"
          onMouseEnter={onPieEnter}
          onMouseLeave={onPieLeave}
          activeIndex={activeIndex as number}
        >
          {data.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={getCategoryColor(entry.name)} 
              strokeWidth={1}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomPieTooltip />} />
        <Legend 
          layout="horizontal" 
          verticalAlign="bottom" 
          align="center"
          formatter={(value) => <span className="font-subtitle text-sm">{value}</span>}
        />
      </PieChart>
    </ResponsiveContainer>
  );

  // Staff analytics dashboard component
  const StaffAnalyticsDashboard = () => (
    <div className="space-y-8">
      <div className="flex justify-end mb-4">
        <div className="inline-flex rounded-md shadow-sm">
          <Button 
            variant={timeRange === 'week' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTimeRange('week')}
            className="font-section"
          >
            Week
          </Button>
          <Button 
            variant={timeRange === 'month' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTimeRange('month')}
            className="font-section"
          >
            Month
          </Button>
          <Button 
            variant={timeRange === 'year' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTimeRange('year')}
            className="font-section"
          >
            Year
          </Button>
          <Button 
            variant={timeRange === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTimeRange('all')}
            className="font-section"
          >
            All Time
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium font-title">Courses</CardTitle>
            <BookOpen className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-title text-primary">{staffAnalytics.coursesByCategory.reduce((sum, item) => sum + Number(item.value), 0)}</div>
            <p className="text-xs text-muted-foreground font-caption">
              Total courses
            </p>
          </CardContent>
        </Card>

        <Card className="hover-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium font-title">Registrations</CardTitle>
            <Users className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-title text-primary">
              {staffAnalytics.registrationsTrend.reduce((sum, item) => sum + item.registrations, 0)}
            </div>
            <p className="text-xs text-muted-foreground font-caption">
              Total registrations
            </p>
          </CardContent>
        </Card>

        <Card className="hover-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium font-title">Course Types</CardTitle>
            <Compass className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-title text-primary">{staffAnalytics.courseTypeDistribution.length}</div>
            <p className="text-xs text-muted-foreground font-caption">
              Event formats
            </p>
          </CardContent>
        </Card>

        <Card className="hover-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium font-title">Categories</CardTitle>
            <GraduationCap className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-title text-primary">{staffAnalytics.coursesByCategory.length}</div>
            <p className="text-xs text-muted-foreground font-caption">
              Course categories
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-title">Course Categories</CardTitle>
              <CardDescription className="font-subtitle">
                Distribution of courses by category
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-1"
              onClick={() => navigate('/analytics/categories')}
            >
              <ExternalLink className="h-3 w-3" />
              <span className="font-section">Detailed View</span>
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-80">
              {renderSimplePieChart(staffAnalytics.coursesByCategory)}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="font-title">Course Type Distribution</CardTitle>
            <CardDescription className="font-subtitle">
              Live events vs. self-paced courses
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={staffAnalytics.courseTypeDistribution}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <YAxis tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Tooltip contentStyle={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Bar dataKey="value" fill="#004161">
                    {staffAnalytics.courseTypeDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={BRAND_COLORS[index % BRAND_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="font-title">Registration Trends</CardTitle>
            <CardDescription className="font-subtitle">
              Course registrations over time
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={staffAnalytics.registrationsTrend}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <YAxis tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Tooltip contentStyle={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Legend formatter={(value) => <span className="font-subtitle">{value}</span>} />
                  <Line
                    type="monotone"
                    dataKey="registrations"
                    stroke="#0f8f8c"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="font-title">Attendance Trends</CardTitle>
            <CardDescription className="font-subtitle">
              Total registrations vs. attendance
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={staffAnalytics.attendanceTrend}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <YAxis tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Tooltip contentStyle={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Legend formatter={(value) => <span className="font-subtitle">{value}</span>} />
                  <Area
                    type="monotone"
                    dataKey="total"
                    stackId="1"
                    stroke="#004161"
                    fill="#004161"
                  />
                  <Area
                    type="monotone"
                    dataKey="attended"
                    stackId="2"
                    stroke="#0f8f8c"
                    fill="#0f8f8c"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // User analytics dashboard component
  const UserAnalyticsDashboard = () => (
    <div className="space-y-8">
      <div className="flex justify-end mb-4">
        <div className="inline-flex rounded-md shadow-sm">
          <Button 
            variant={timeRange === 'week' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTimeRange('week')}
            className="font-section"
          >
            Week
          </Button>
          <Button 
            variant={timeRange === 'month' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTimeRange('month')}
            className="font-section"
          >
            Month
          </Button>
          <Button 
            variant={timeRange === 'year' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTimeRange('year')}
            className="font-section"
          >
            Year
          </Button>
          <Button 
            variant={timeRange === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTimeRange('all')}
            className="font-section"
          >
            All Time
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium font-title">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-title text-primary">{userAnalytics.progressStats.completed}</div>
            <p className="text-xs text-muted-foreground font-caption">
              Courses completed
            </p>
          </CardContent>
        </Card>

        <Card className="hover-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium font-title">In Progress</CardTitle>
            <Clock className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-title text-primary">{userAnalytics.progressStats.inProgress}</div>
            <p className="text-xs text-muted-foreground font-caption">
              Courses in progress
            </p>
          </CardContent>
        </Card>

        <Card className="hover-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium font-title">Registered</CardTitle>
            <Calendar className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-title text-primary">{userAnalytics.progressStats.registered}</div>
            <p className="text-xs text-muted-foreground font-caption">
              Upcoming courses
            </p>
          </CardContent>
        </Card>

        <Card className="hover-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium font-title">Completion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-title text-primary">
              {userAnalytics.progressStats.total > 0 
                ? `${Math.round((userAnalytics.progressStats.completed / userAnalytics.progressStats.total) * 100)}%` 
                : '0%'}
            </div>
            <p className="text-xs text-muted-foreground font-caption">
              Completion rate
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-title">Course Categories</CardTitle>
            <CardDescription className="font-subtitle">
              Your courses by category
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-80">
              {renderSimplePieChart(userAnalytics.coursesCategoryDistribution)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-title">Completed Courses</CardTitle>
            <CardDescription className="font-subtitle">
              Your course completions over time
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={userAnalytics.completedCoursesTrend}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <YAxis tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Tooltip contentStyle={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Legend formatter={(value) => <span className="font-subtitle">{value}</span>} />
                  <Line
                    type="monotone"
                    dataKey="completed"
                    stroke="#0f8f8c"
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-title">CEU Hours Completed</CardTitle>
            <CardDescription className="font-subtitle">
              Hours of professional development completed
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={userAnalytics.hoursCompletedTrend}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <YAxis tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Tooltip contentStyle={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                  <Area
                    type="monotone"
                    dataKey="hours"
                    stroke="#0f8f8c"
                    fill="#0f8f8c"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="w-full py-1 px-1 space-y-4">
      <div className="page-header">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-[#004161]" />
          <h1>Learning Analytics</h1>
        </div>
        <p>
          {isStaff 
            ? "View program statistics and user engagement metrics." 
            : "Track your learning progress and professional development journey."}
        </p>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger className="font-section" value="overview">Overview</TabsTrigger>
          {isStaff && <TabsTrigger className="font-section" value="courses">Courses</TabsTrigger>}
          {isStaff && <TabsTrigger className="font-section" value="users">Users</TabsTrigger>}
          {!isStaff && <TabsTrigger className="font-section" value="progress">My Progress</TabsTrigger>}
          {!isStaff && <TabsTrigger className="font-section" value="certificates">Certificates</TabsTrigger>}
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          {isLoadingStaffData || isLoadingUserData ? (
            <div className="flex justify-center items-center p-12">
              <p className="text-muted-foreground">Loading analytics data...</p>
            </div>
          ) : isStaff ? <StaffAnalyticsDashboard /> : <UserAnalyticsDashboard />}
        </TabsContent>
        
        {isStaff && (
          <TabsContent value="courses" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="font-title">Course Enrollment Trends</CardTitle>
                <CardDescription className="font-subtitle">
                  Enrollment rates across all courses
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={staffAnalytics.registrationsTrend}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <YAxis tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <Tooltip contentStyle={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <Legend formatter={(value) => <span className="font-subtitle">{value}</span>} />
                      <Bar dataKey="registrations" name="Registrations" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {isStaff && (
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="font-title">User Engagement</CardTitle>
                <CardDescription className="font-subtitle">
                  User activity and participation rates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={staffAnalytics.attendanceTrend}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <YAxis tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <Tooltip contentStyle={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <Legend formatter={(value) => <span className="font-subtitle">{value}</span>} />
                      <Line
                        type="monotone"
                        dataKey="total"
                        name="Total Registrations"
                        stroke="#8884d8"
                        activeDot={{ r: 8 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="attended"
                        name="Attended"
                        stroke="#82ca9d"
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {!isStaff && (
          <TabsContent value="progress" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="font-title">Learning Progress</CardTitle>
                <CardDescription className="font-subtitle">
                  Your learning journey progress
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={userAnalytics.completedCoursesTrend}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <YAxis tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <Tooltip contentStyle={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <Legend formatter={(value) => <span className="font-subtitle">{value}</span>} />
                      <Line
                        type="monotone"
                        dataKey="completed"
                        name="Completed Courses"
                        stroke="#8884d8"
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {!isStaff && (
          <TabsContent value="certificates" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="font-title">CEU Hours Earned</CardTitle>
                <CardDescription className="font-subtitle">
                  Your accumulated professional development hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={userAnalytics.hoursCompletedTrend}
                      margin={{
                        top: 10,
                        right: 30,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <YAxis tick={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <Tooltip contentStyle={{ fontFamily: "'Proxima Nova', sans-serif" }} />
                      <Area
                        type="monotone"
                        dataKey="hours"
                        name="CEU Hours"
                        stroke="#82ca9d"
                        fill="#82ca9d"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Analytics;
